import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { useVerifyInviteToken } from '../hooks/useInvitations';
import { Button } from '../components/Button';

// ============ VALIDATION SCHEMA ============
const registerSchema = z.object({
  firstName: z.string().min(2, 'First name required'),
  lastName: z.string().min(2, 'Last name required'),
  email: z.string().email('Valid email required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

// ============ STYLE CONSTANTS ============
const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';

const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';

const errorClasses = 'text-xs text-error mt-1';

// Default family group for this site
const DEFAULT_FAMILY_GROUP = 'Goldson-Wade';

// ============ INVITE ERROR MESSAGE HELPER ============
function getInviteErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: string }).code;
    if (code === 'INVITE_EXPIRED') return 'This invitation link has expired. Please ask your family member to send a new one.';
    if (code === 'INVITE_ALREADY_USED') return 'This invitation has already been used.';
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: string }).message;
    if (message?.toLowerCase().includes('expired')) return 'This invitation link has expired. Please ask your family member to send a new one.';
    if (message?.toLowerCase().includes('already') || message?.toLowerCase().includes('used')) return 'This invitation has already been used.';
  }
  return 'Invalid invitation link.';
}

// ============ COMPONENT ============
const Register = () => {
  const { currentUser, register: registerUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const inviteToken = searchParams.get('invite');

  // Verify invite token if present
  const { data: inviteData, isLoading: verifyLoading, error: verifyError } = useVerifyInviteToken(inviteToken);
  const invite = inviteData;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  // Pre-fill form when invite data loads
  useEffect(() => {
    if (invite) {
      const nameParts = invite.inviteeName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setValue('firstName', firstName, { shouldValidate: true });
      setValue('lastName', lastName, { shouldValidate: true });
      setValue('email', invite.inviteeEmail, { shouldValidate: true });
    }
  }, [invite, setValue]);

  // Redirect if already authenticated (must be after all hooks)
  if (currentUser) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (data: RegisterFormData) => {
    setServerError(null);
    try {
      await registerUser({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        familyGroup: DEFAULT_FAMILY_GROUP,
        ...(inviteToken ? { inviteToken } : {}),
      });
      setSuccess(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setServerError(message);
    }
  };

  // If invite token is present and still loading, show loading state
  const showInviteLoading = !!inviteToken && verifyLoading;
  // If invite token failed verification, show error but still allow manual registration
  const inviteErrorMessage = verifyError ? getInviteErrorMessage(verifyError) : null;

  return (
    <div className="bg-background">
      {/* ===== SECTION 1: PAGE HERO ===== */}
      <section className="relative py-24 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15" style={{ backgroundImage: "url('/images/goldson-wade-2018-hero.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800/90 to-primary-dark/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-white">Get Started</span>
          </span>
          <h1 className="heading-hero text-white mb-6 max-w-4xl mx-auto">Join the Goldson-Wade Reunion</h1>
          <p className="text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto">
            {invite
              ? `You've been invited to join the ${invite.reunionName}. Create your account to get started.`
              : 'Create your account to connect with the Goldson-Wade family. Our last reunion was in 2018 and we\'re planning the next one — sign up to stay in the loop.'
            }
          </p>
        </div>
      </section>

      {/* ===== SECTION 2: REGISTRATION FORM ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">

          {/* Two-step process explanation */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-5 mb-8">
            <p className="text-sm font-bold text-slate-700 mb-3">How it works — two quick steps:</p>
            <div className="flex items-start gap-3 mb-2">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">1</span>
              <p className="text-sm text-slate-600"><strong>Sign up</strong> — Create your account right here (name, email, password)</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">2</span>
              <p className="text-sm text-slate-600"><strong>Register your family</strong> — After signing in, complete the registration form with your group details</p>
            </div>
          </div>

          {/* Invite Loading State */}
          {showInviteLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-slate-600 text-sm">Verifying your invitation...</p>
            </div>
          )}

          {/* Invite Error State */}
          {inviteErrorMessage && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-6 mb-8">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="text-red-800 font-medium">{inviteErrorMessage}</p>
                  <p className="text-red-600 text-sm mt-2">
                    You can still <Link to="/register" className="underline font-medium">register without an invitation</Link>.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Invite Context Banner */}
          {invite && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-6 mb-8">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-blue-800">
                    You've been invited by <strong>{invite.inviterName}</strong> to join <strong>{invite.reunionName}</strong>
                  </p>
                  {invite.personalNote && (
                    <p className="mt-2 text-sm text-blue-700 italic">"{invite.personalNote}"</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {!showInviteLoading && (
            <>
              {success ? (
                /* -- Success State -- */
                <div className="rounded-lg bg-success-light border border-success-border p-8 text-center">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Account Created!</h3>
                  <p className="text-slate-700 text-base font-medium mb-2">
                    Before you can log in, you need to verify your email.
                  </p>
                  <div className="rounded-md bg-white border border-slate-200 p-4 mb-4 text-left">
                    <p className="text-sm text-slate-700 mb-2"><strong>Here's what to do:</strong></p>
                    <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                      <li>Open your email inbox (check spam/junk too)</li>
                      <li>Find the email from <strong>Goldson-Wade Reunion</strong></li>
                      <li>Click the <strong>"Verify Email"</strong> button in that email</li>
                      <li>Once verified, come back here and log in</li>
                    </ol>
                  </div>
                  <Link
                    to="/login"
                    className="inline-flex items-center text-primary font-bold hover:underline"
                  >
                    Go to Log In
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </div>
              ) : (
                /* -- Registration Form -- */
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                  {/* Server Error */}
                  {serverError && (
                    <div className="rounded-lg bg-error/10 border border-error/20 p-4 text-sm text-error">
                      {serverError}
                    </div>
                  )}

                  {/* First Name + Last Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="reg-firstName" className={labelClasses}>First Name</label>
                      <input
                        id="reg-firstName"
                        type="text"
                        placeholder="First name"
                        className={inputClasses}
                        {...register('firstName')}
                      />
                      {errors.firstName && <p className={errorClasses}>{errors.firstName.message}</p>}
                    </div>
                    <div>
                      <label htmlFor="reg-lastName" className={labelClasses}>Last Name</label>
                      <input
                        id="reg-lastName"
                        type="text"
                        placeholder="Last name"
                        className={inputClasses}
                        {...register('lastName')}
                      />
                      {errors.lastName && <p className={errorClasses}>{errors.lastName.message}</p>}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="reg-email" className={labelClasses}>Email</label>
                    <input
                      id="reg-email"
                      type="email"
                      placeholder="you@example.com"
                      className={inputClasses}
                      {...register('email')}
                    />
                    {errors.email && <p className={errorClasses}>{errors.email.message}</p>}
                  </div>

                  {/* Password + Confirm Password */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="reg-password" className={labelClasses}>Password</label>
                      <input
                        id="reg-password"
                        type="password"
                        placeholder="Min 8 characters"
                        className={inputClasses}
                        {...register('password')}
                      />
                      {errors.password && <p className={errorClasses}>{errors.password.message}</p>}
                    </div>
                    <div>
                      <label htmlFor="reg-confirmPassword" className={labelClasses}>Confirm Password</label>
                      <input
                        id="reg-confirmPassword"
                        type="password"
                        placeholder="Re-enter password"
                        className={inputClasses}
                        {...register('confirmPassword')}
                      />
                      {errors.confirmPassword && <p className={errorClasses}>{errors.confirmPassword.message}</p>}
                    </div>
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating Account...' : 'Create Account'}
                    {!isSubmitting && (
                      <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    )}
                  </Button>

                  {/* Legal Notice */}
                  <p className="text-xs text-slate-500 text-center mt-4">
                    By creating an account, you agree to our{' '}
                    <Link to="/terms" className="text-primary hover:underline">Terms & Conditions</Link>
                    {' and '}
                    <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
                  </p>

                  {/* Login Link */}
                  <p className="text-center text-sm text-slate-600">
                    Already have an account?{' '}
                    <Link to="/login" className="text-primary font-bold hover:underline">
                      Log in
                    </Link>
                  </p>
                </form>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Register;
